import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'enpiso_kanban_tts_voice_v1';
const DEFAULT_LANG = 'es-CO';

const readStoredVoiceURI = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
};

const storeVoiceURI = (voiceURI) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, voiceURI || '');
  } catch (error) {
    // almacenamiento no disponible
  }
};

const isSpanishVoice = (voice) => (voice.lang || '').toLowerCase().startsWith('es');

// Voz seleccionada por el usuario para leer descripciones y comentarios del kanban.
// Se guarda en localStorage para que persista entre tarjetas y sesiones.
export const useTtsVoice = () => {
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURIState] = useState(readStoredVoiceURI);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const loadVoices = () => {
      setVoices(window.speechSynthesis.getVoices().filter(isSpanishVoice));
    };
    loadVoices();
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
  }, []);

  const setVoiceURI = useCallback((nextVoiceURI) => {
    setVoiceURIState(nextVoiceURI);
    storeVoiceURI(nextVoiceURI);
  }, []);

  const selectedVoice = voices.find((voice) => voice.voiceURI === voiceURI) || null;

  return { voices, voiceURI, selectedVoice, setVoiceURI };
};

// Los sintetizadores de voz en español suelen leer mal símbolos sueltos
// (#, /, @, &, _, *): esto los reemplaza por su forma hablada o los descarta.
export const sanitizeForSpeech = (text) => {
  if (!text) return '';
  return text
    .replace(/\{\{\s*([^}]+?)\s*\}\}/g, '$1')
    .replace(/#/g, ' numeral ')
    .replace(/\//g, ' barra ')
    .replace(/&/g, ' y ')
    .replace(/@/g, ' arroba ')
    .replace(/[_*~`]/g, ' ')
    .replace(/[•▪●·]/g, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{2,}/g, '\n')
    .trim();
};

export const speakWithVoice = (rawText, { voice, onEnd } = {}) => {
  if (!window.speechSynthesis) return false;
  const clean = sanitizeForSpeech(rawText);
  if (!clean) return false;

  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(clean);
  utterance.lang = voice?.lang || DEFAULT_LANG;
  if (voice) utterance.voice = voice;
  utterance.onend = () => onEnd?.();
  utterance.onerror = () => onEnd?.();
  window.speechSynthesis.speak(utterance);
  return true;
};
